import React, { useRef } from "react";
import { ContinuousScaleAnswerFormat, ScaleAnswerFormat, SurveyFormStyle } from "../../../../types/Survey";
import "../../SurveyVars.css"
import "./Scale.css"
import ScaleScreenReader from "./ScaleScreenReader";
import { createHelpers } from "./ScaleHelper";

export interface ScaleProps {
    answerFormat: ScaleAnswerFormat;
    value: string;
    onChange?: (value: string) => void;
    topBorder?: boolean;
    variant?: SurveyFormStyle
    inputIdForLabel?: string;
}

export default function Scale(props: ScaleProps) {
    let scaleLineRef = useRef<HTMLDivElement>(null);

    let minimumValue = props.answerFormat.minimumValue || 0;
    let maximumValue = (!props.answerFormat.maximumValue && props.answerFormat.maximumValue != 0) ? 10 : props.answerFormat.maximumValue;
    var range = maximumValue - minimumValue;
    let step = props.answerFormat.step || 1;

    const { getMaximumFractionDigits, formatValue, getMinimumStepValue } = createHelpers(props.answerFormat);

    function getAnswerMarker() {
        if (props.value === "") {
            return null;
        }
        var value = parseFloat(props.value);
        var position = ((value - minimumValue) / range) * 100;

        let styles: React.CSSProperties = {};
        if (props.answerFormat.vertical) {
            styles.top = 100 - position + "%";
        } else {
            styles.left = position + "%";
        }

        return <div className="answer-marker-wrapper" style={styles}>
            <div className="answer-marker"></div>
        </div>
    }

    let ticks: number[] = [];
    if (props.answerFormat.type == "ScaleAnswerFormat") {
        for (var i = step; i < range; i += step) {
            let percentage = (i * 1.0 / range) * 100;
            ticks.push(percentage);
        }
    }

    function updateScalePosition(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        function setAnswerInPercent(percent: number) {
            var targetValue = range * percent + minimumValue;
            if (targetValue < minimumValue) {
                targetValue = minimumValue;
            }

            if (targetValue > maximumValue) {
                targetValue = maximumValue;
            }

            setValue(targetValue);
        }

        let boundingRect = scaleLineRef.current?.getBoundingClientRect();
        if (!boundingRect) {
            return;
        }
        if (props.answerFormat.vertical) {
            let height = boundingRect.height;
            var y = e.clientY - boundingRect.top;
            var percent = 1 - (y / height);
            setAnswerInPercent(percent);
        } else {
            let width = boundingRect.width;
            let x = e.clientX - boundingRect.left;
            let percent = x / width;
            setAnswerInPercent(percent);
        }
    }

    function setValue(value: string | number) {
        if (typeof value === "number") {
            props.onChange?.(value.toFixed(getMaximumFractionDigits()));
        } else if (typeof value === "string") {
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
                props.onChange?.(parsed.toFixed(getMaximumFractionDigits()));
            }
        }
    }


    let classes = ["scale-wrapper"];
    if (props.topBorder) {
        classes.push("scale-wrapper-top-border");
    }
    if (props.variant == "cardBased") {
        classes.push("scale-wrapper-card-based");
    }

    return <div className={classes.join(" ")}>
        <div className="scale-border">

            {/* Hidden range input for screen readers and keyboard navigation.
                Both elements sync via the same props.value and setValue() handler. */}
            <ScaleScreenReader
                value={props.value}
                minimumValue={minimumValue}
                maximumValue={maximumValue}
                range={range}
                step={getMinimumStepValue()}
                answerFormat={props.answerFormat}
                inputIdForLabel={props.inputIdForLabel || ""}
                onChange={setValue} />

            <div className="selected-value-display">
                {formatValue(props.value)}
            </div>
            {/* Visual custom scale with click interaction - hidden from screen readers.
                Synced with ScaleScreenReader above via shared props.value state. */}
            <div
                className={props.answerFormat.vertical ? "scale-vertical" : "scale-horizontal"}
                aria-hidden="true">
                <div className="value min-value">
                    {formatValue(minimumValue.toString())}
                </div>
                <div className="scale">
                    {props.answerFormat.type == "ScaleAnswerFormat" &&
                        <div className="tick first-tick" />
                    }
                    <div ref={scaleLineRef} className="scale-line-wrapper horizontal" onClick={(e) => updateScalePosition(e)}>
                        <div className="scale-line"></div>
                        <div className="click-area"></div>
                    </div>
                    {props.answerFormat.type == "ScaleAnswerFormat" &&
                        <div className="tick last-tick"></div>
                    }
                    {getAnswerMarker()}
                    {ticks.map(tick =>
                        <div key={tick} className='tick' style={{
                            top: props.answerFormat.vertical ? tick + "%" : undefined,
                            left: !props.answerFormat.vertical ? tick + "%" : undefined
                        }}></div>
                    )}
                </div>
                <div className="value max-value">
                    {formatValue(maximumValue.toString())}
                </div>
                <div className="descriptions">
                    <div className="description min-value-description">{props.answerFormat.minimumValueDescription}</div>
                    <div className="description max-value-description">{props.answerFormat.maximumValueDescription}</div>
                    <div style={{ clear: "both" }}></div>
                </div>
            </div>
        </div>
    </div >;
}